import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'

const routes: RouteRecordRaw[] = [
  // 认证路由（不需要登录）
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { public: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/pages/auth/RegisterPage.vue'),
    meta: { public: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
    meta: { public: true }
  },
  
  // 主布局（含底部 Tab）
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      // 首页/市场
      {
        path: '',
        name: 'Home',
        component: () => import('@/pages/home/HomePage.vue'),
      },
      {
        path: 'home',
        name: 'HomeTab',
        component: () => import('@/pages/home/HomePage.vue'),
      },
      
      // 持仓
      {
        path: 'positions',
        name: 'Positions',
        component: () => import('@/pages/positions/PositionsPage.vue'),
      },
      
      // 交易 - 重定向到首页
      {
        path: 'trade',
        name: 'Trade',
        component: () => import('@/pages/trade/RedirectPage.vue'),
      },
      
      // 个人中心
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/pages/profile/ProfilePage.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.isBangBetUser) {
            // Redirect BangBet users to home
            next('/')
          } else {
            next()
          }
        }
      },
    ]
  },
  
  // 市场模块（独立页面，不含 Tab）
  {
    path: '/market/:id',
    name: 'MarketDetail',
    component: () => import('@/pages/market/DetailPage.vue'),
    props: true,
    meta: { requiresAuth: true }
  },
  
  // 资金模块（独立页面）
  {
    path: '/wallet',
    name: 'Wallet',
    component: () => import('@/pages/wallet/WalletPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/wallet/deposit',
    name: 'Deposit',
    component: () => import('@/pages/wallet/DepositPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/wallet/deposit/result',
    name: 'DepositResult',
    component: () => import('@/pages/wallet/DepositResultPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/wallet/withdraw',
    name: 'Withdraw',
    component: () => import('@/pages/wallet/WithdrawPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/wallet/transactions',
    name: 'Transactions',
    component: () => import('@/pages/wallet/TransactionsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/wallet/cards',
    name: 'BankCards',
    component: () => import('@/pages/wallet/BankCardsPage.vue'),
    meta: { requiresAuth: true }
  },
  
  // 订单模块
  {
    path: '/orders',
    name: 'Orders',
    component: () => import('@/pages/orders/OrdersPage.vue'),
    meta: { requiresAuth: true }
  },
  
  // 通知模块
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('@/pages/notifications/NotificationsPage.vue'),
    meta: { requiresAuth: true }
  },
  
  // 个人设置模块
  {
    path: '/profile/kyc',
    name: 'KYC',
    component: () => import('@/pages/profile/KYCPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/security',
    name: 'Security',
    component: () => import('@/pages/profile/SecurityPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/change-password',
    name: 'ChangePassword',
    component: () => import('@/pages/profile/ChangePasswordPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/settings',
    name: 'ProfileSettings',
    component: () => import('@/pages/profile/SettingsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile/help',
    name: 'Help',
    component: () => import('@/pages/profile/HelpPage.vue'),
    meta: { requiresAuth: true }
  },
  
  // 争议模块
  {
    path: '/disputes',
    name: 'Disputes',
    component: () => import('@/pages/trade/TradePage.vue'),
    meta: { requiresAuth: true }
  },
  
  // 404页面
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/pages/NotFoundPage.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// 路由守卫
router.beforeEach(async (to, from, next) => {
  console.log('Router guard - to:', to.path, 'from:', from.path)
  
  const authStore = useAuthStore()
  
  // 首页和市场列表页不需要登录（公开浏览）
  const publicPaths = ['/', '/home', '/login', '/register', '/forgot-password']
  if (publicPaths.includes(to.path)) {
    next()
    return
  }
  
  // 直接从 localStorage 检查 token（不依赖 authStore 状态）
  const token = localStorage.getItem('access_token')
  console.log('Router guard - token exists:', !!token)
  
  // 未登录用户访问需要认证的页面，重定向到登录页
  if (to.meta.requiresAuth && !token) {
    console.log('Router guard - no token, redirecting to /login')
    next('/login')
    return
  }
  
  // 已登录用户访问公开页面（如登录页），重定向到首页
  if (to.meta.public && token) {
    next('/')
    return
  }
  
  // 如果有 token 但 authStore 没有 user，尝试获取用户信息
  if (token && !authStore.user) {
    try {
      await authStore.fetchCurrentUser()
    } catch (err: any) {
      // 只在 401 错误时清除 token 并跳转登录
      const status = err.response?.status
      if (status === 401) {
        authStore.clearTokens()
        next('/login')
        return
      }
      // 其他错误不跳转，允许继续访问
    }
  }

  next()
})

export default router
